import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Route, X, GripVertical, Trash2, MapPin, Navigation,
  ExternalLink, Loader2, Zap, ArrowUpDown, Truck, Play
} from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_ADDRESS = '266 Osborne Avenue, Clayton South VIC 3169';

const RoutePlanner = ({ selectedStops, onRemoveStop, onReorderStops, onClose, drivers, onClearStops }) => {
  const [startAddress, setStartAddress] = useState(DEFAULT_ADDRESS);
  const [endAddress, setEndAddress] = useState(DEFAULT_ADDRESS);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeMode, setOptimizeMode] = useState('auto');
  const [assignedDriver, setAssignedDriver] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [driveTimeMins, setDriveTimeMins] = useState(null);
  const [totalDistanceKm, setTotalDistanceKm] = useState(null);

  const token = localStorage.getItem('admin_token');

  // Auto-optimize via Google Directions API
  const handleOptimize = async () => {
    if (selectedStops.length < 2) return;
    setOptimizing(true);
    try {
      const result = await safeFetch(`${API_URL}/api/admin/route-planner/optimize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_address: startAddress,
          end_address: endAddress,
          waypoints: selectedStops.map(s => ({
            id: s.id,
            booking_ref: s.booking_ref,
            address: s.waste_details?.site_address || s.delivery_details?.delivery_address || '',
            lat: s.lat,
            lng: s.lng
          }))
        })
      });
      if (result.ok && result.data?.optimized_order) {
        const order = result.data.optimized_order;
        const reordered = order.map(idx => selectedStops[idx]).filter(Boolean);
        onReorderStops(reordered);
        setDriveTimeMins(result.data.total_duration_mins || null);
        setTotalDistanceKm(result.data.total_distance_km || null);
      }
    } catch (err) {
      console.error('Optimize failed:', err);
    } finally {
      setOptimizing(false);
    }
  };

  // Save route and assign to driver
  const handleSaveRoute = async () => {
    if (!assignedDriver) return;
    setSaving(true);
    try {
      await safeFetch(`${API_URL}/api/admin/route-planner/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driver_id: assignedDriver,
          start_address: startAddress,
          end_address: endAddress,
          stops: selectedStops.map((s, i) => ({
            booking_id: s.id,
            booking_ref: s.booking_ref,
            address: s.waste_details?.site_address || s.delivery_details?.delivery_address || '',
            lat: s.lat,
            lng: s.lng,
            order: i
          }))
        })
      });
    } catch (err) {
      console.error('Save route failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // Open in Google Maps
  const openInGoogleMaps = () => {
    const origin = encodeURIComponent(startAddress);
    const destination = encodeURIComponent(endAddress);
    const waypoints = selectedStops
      .map(s => {
        if (s.lat && s.lng) return `${s.lat},${s.lng}`;
        return encodeURIComponent(s.waste_details?.site_address || s.delivery_details?.delivery_address || '');
      })
      .join('|');

    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Drag and drop handlers
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const items = [...selectedStops];
    const [removed] = items.splice(dragIdx, 1);
    items.splice(idx, 0, removed);
    onReorderStops(items);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  return (
    <Card className="w-80 flex-shrink-0 overflow-y-auto border-0 shadow-lg" data-testid="route-planner-panel">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0D9488]/10 flex items-center justify-center">
              <Route className="w-4 h-4 text-[#0D9488]" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-[#111111]">Route Planner</h3>
              <p className="text-[10px] text-[#94A3B8]">Click pins on map to add stops</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-route-planner">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Start Address */}
        <div>
          <label className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide flex items-center gap-1 mb-1">
            <div className="w-3 h-3 rounded-full bg-green-500" /> Start
          </label>
          <Input
            value={startAddress}
            onChange={e => setStartAddress(e.target.value)}
            placeholder="Starting address..."
            className="text-xs h-8"
            data-testid="route-start-address"
          />
        </div>

        {/* Optimization Mode */}
        <div className="flex gap-1 p-0.5 bg-[#F1F5F9] rounded-lg">
          <button
            onClick={() => setOptimizeMode('auto')}
            className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
              optimizeMode === 'auto' ? 'bg-white text-[#0D9488] shadow-sm' : 'text-[#64748B]'
            }`}
            data-testid="mode-auto"
          >
            <Zap className="w-3 h-3 inline mr-1" />Auto-Optimize
          </button>
          <button
            onClick={() => setOptimizeMode('manual')}
            className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
              optimizeMode === 'manual' ? 'bg-white text-[#0D9488] shadow-sm' : 'text-[#64748B]'
            }`}
            data-testid="mode-manual"
          >
            <ArrowUpDown className="w-3 h-3 inline mr-1" />Manual Order
          </button>
        </div>

        {/* Stops List */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide">
              Stops ({selectedStops.length})
            </span>
            {selectedStops.length > 0 && (
              <button onClick={onClearStops} className="text-[10px] text-red-400 hover:text-red-600" data-testid="clear-stops">
                Clear all
              </button>
            )}
          </div>

          {selectedStops.length === 0 ? (
            <div className="py-6 text-center border-2 border-dashed border-[#E2E8F0] rounded-lg">
              <MapPin className="w-6 h-6 text-[#CBD5E1] mx-auto mb-1" />
              <p className="text-xs text-[#94A3B8]">Click pins on the map to add stops</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {selectedStops.map((stop, idx) => (
                <div
                  key={stop.id}
                  draggable={optimizeMode === 'manual'}
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                    dragIdx === idx ? 'border-[#0D9488] bg-[#F0FDFA]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
                  }`}
                  data-testid={`route-stop-${idx}`}
                >
                  {optimizeMode === 'manual' && (
                    <GripVertical className="w-3 h-3 text-[#CBD5E1] cursor-grab flex-shrink-0" />
                  )}
                  <div className="w-5 h-5 rounded-full bg-[#0D9488] flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] text-white font-bold">{idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-[#111111] truncate">{stop.booking_ref}</p>
                    <p className="text-[10px] text-[#94A3B8] truncate">
                      {stop.waste_details?.site_address || stop.delivery_details?.delivery_address || 'No address'}
                    </p>
                  </div>
                  <button
                    onClick={() => onRemoveStop(stop.id)}
                    className="text-[#CBD5E1] hover:text-red-400 flex-shrink-0"
                    data-testid={`remove-stop-${idx}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* End Address */}
        <div>
          <label className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide flex items-center gap-1 mb-1">
            <div className="w-3 h-3 rounded-full bg-red-500" /> End
          </label>
          <Input
            value={endAddress}
            onChange={e => setEndAddress(e.target.value)}
            placeholder="Ending address..."
            className="text-xs h-8"
            data-testid="route-end-address"
          />
        </div>

        {/* Trip Estimate */}
        {selectedStops.length > 0 && (
          <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0]" data-testid="trip-estimate">
            <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide mb-2">Trip Estimate</p>
            {driveTimeMins !== null ? (
              <>
                <div className="grid grid-cols-3 gap-2 text-center mb-2">
                  <div>
                    <p className="text-lg font-bold text-[#0D9488]">{driveTimeMins}</p>
                    <p className="text-[9px] text-[#94A3B8]">Drive (min)</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#6366F1]">{selectedStops.length * 15}</p>
                    <p className="text-[9px] text-[#94A3B8]">Stops (min)</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#F59E0B]">30</p>
                    <p className="text-[9px] text-[#94A3B8]">Break (min)</p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
                  <span className="text-xs font-semibold text-[#111111]">Total Estimated Time</span>
                  <span className="text-sm font-bold text-[#0D9488]" data-testid="total-trip-time">
                    {(() => {
                      const total = driveTimeMins + (selectedStops.length * 15) + 30;
                      const hrs = Math.floor(total / 60);
                      const mins = total % 60;
                      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                    })()}
                  </span>
                </div>
                {totalDistanceKm && (
                  <p className="text-[10px] text-[#94A3B8] text-right mt-1">{totalDistanceKm} km total distance</p>
                )}
              </>
            ) : (
              <div className="text-center py-2">
                <p className="text-xs text-[#94A3B8]">
                  {selectedStops.length >= 2
                    ? 'Click "Optimize Route Order" to calculate estimate'
                    : 'Add at least 2 stops to estimate'
                  }
                </p>
                <p className="text-[10px] text-[#CBD5E1] mt-1">
                  {selectedStops.length * 15} min stops + 30 min break + drive time
                </p>
              </div>
            )}
          </div>
        )}

        {/* Assign to Driver */}
        <div>
          <label className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide flex items-center gap-1 mb-1">
            <Truck className="w-3 h-3" /> Assign to Driver
          </label>
          <select
            value={assignedDriver}
            onChange={e => setAssignedDriver(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            data-testid="route-driver-select"
          >
            <option value="">-- Select Driver --</option>
            {(drivers || []).filter(d => d.status !== 'offline' && d.active !== false).map(d => (
              <option key={d.id} value={d.id}>{d.name}{d.vehicle_type ? ` (${d.vehicle_type})` : ''}</option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="space-y-2 pt-2 border-t border-[#F1F5F9]">
          {optimizeMode === 'auto' && selectedStops.length >= 2 && (
            <Button
              size="sm"
              onClick={handleOptimize}
              disabled={optimizing}
              className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs h-9"
              data-testid="optimize-route-btn"
            >
              {optimizing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
              Optimize Route Order
            </Button>
          )}

          <Button
            size="sm"
            onClick={openInGoogleMaps}
            disabled={selectedStops.length === 0}
            className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white text-xs h-9"
            data-testid="open-google-maps-btn"
          >
            <Navigation className="w-3 h-3 mr-1" />
            Open in Google Maps
          </Button>

          {assignedDriver && selectedStops.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveRoute}
              disabled={saving}
              className="w-full text-xs h-9 border-[#0D9488] text-[#0D9488] hover:bg-[#F0FDFA]"
              data-testid="save-route-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Save & Assign Route
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RoutePlanner;
